import { useState, useEffect, useMemo } from "react";
import DynamicElements from "@/components/DynamicElements";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import CryptoCard from "@/components/CryptoCard";
import PurchaseModal from "@/components/PurchaseModal";

import { CryptoAsset } from "@/lib/crypto-data";
import { useCryptoPrices } from "@/hooks/useCryptoPrices";
import { useAuth } from "@/hooks/useAuth";

import { useTranslation } from "react-i18next";
import { Wallet, TrendingUp, CreditCard, RefreshCw, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

const Dashboard = () => {
  const { user, loading: authLoading } = useAuth();
  
  const { cryptoAssets, loading: pricesLoading, lastUpdated, refetch } = useCryptoPrices();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedCrypto, setSelectedCrypto] = useState<CryptoAsset | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(30);


  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  const filteredAssets = useMemo(() => {
    if (!searchQuery.trim()) return cryptoAssets;
    const q = searchQuery.toLowerCase();
    return cryptoAssets.filter(
      (c) =>
        c.symbol.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        c.nameAr.includes(q)
    );
  }, [cryptoAssets, searchQuery]);

  const visibleAssets = useMemo(
    () => searchQuery.trim() ? filteredAssets : filteredAssets.slice(0, visibleCount),
    [filteredAssets, visibleCount, searchQuery]
  );

  const handleBuy = (crypto: CryptoAsset) => {
    setSelectedCrypto(crypto);
    setIsModalOpen(true);
  };

  const handlePurchaseSuccess = () => {
    setIsModalOpen(false);
    setSelectedCrypto(null);
  };

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + 30);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary">{t('common.loading')}</div>
      </div>
    );
  }

  const bestPerformer = cryptoAssets.length > 0
    ? cryptoAssets.reduce((best, current) =>
        current.change24h > best.change24h ? current : best
      , cryptoAssets[0])
    : null;

  return (
    <div className="min-h-screen bg-background">
      <Header />



      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/3 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/3 w-[600px] h-[600px] bg-crypto-purple/5 rounded-full blur-3xl" />
      </div>

      <main className="container mx-auto px-4 pt-24 pb-12 relative z-10">
        <DynamicElements position="dashboard" />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 animate-fade-in">

          <div className="glass rounded-xl p-6 shadow-card">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-crypto-green/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-crypto-green" />
              </div>
              <div>
                <p className="text-muted-foreground text-sm">{t('dashboard.bestPerformance')}</p>
                <p className="text-2xl font-bold text-foreground">
                  {bestPerformer
                    ? `${bestPerformer.symbol} ${bestPerformer.change24h > 0 ? '+' : ''}${bestPerformer.change24h.toFixed(2)}%`
                    : '—'}
                </p>
              </div>
            </div>
          </div>

          <div className="glass rounded-xl p-6 shadow-card">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-crypto-gold/20 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-crypto-gold" />
              </div>
              <div>
                <p className="text-muted-foreground text-sm">{t('dashboard.paymentMethods')}</p>
                <p className="text-2xl font-bold text-foreground">{t('dashboard.allCards')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Section Header with Search */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-2">{t('dashboard.availableCryptosTitle')}</h2>
            <p className="text-muted-foreground">
              {t('dashboard.chooseCrypto')}
              {lastUpdated && (
                <span className="text-xs mr-2">
                  ({t('dashboard.lastUpdate')}: {lastUpdated.toLocaleTimeString()})
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={t('dashboard.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setVisibleCount(30);
                  }}
                  className="pr-10 bg-input border-border"
                />
              </div>
              <button
                onClick={() => setVisibleCount(30)}
                className="px-4 py-2 rounded-lg gradient-primary text-primary-foreground hover:opacity-90 transition-opacity shrink-0"
              >
                <Search className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={refetch}
              disabled={pricesLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors disabled:opacity-50 shrink-0"
            >
              <RefreshCw className={`w-4 h-4 ${pricesLoading ? 'animate-spin' : ''}`} />
              {t('dashboard.refresh')}
            </button>
          </div>
        </div>

        {/* Crypto Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {visibleAssets.map((crypto, index) => (
            <CryptoCard key={`${crypto.symbol}-${index}`} crypto={crypto} onBuy={handleBuy} />
          ))}
        </div>

        {/* Load More */}
        {visibleCount < filteredAssets.length && (
          <div className="flex justify-center mt-8">
            <button
              onClick={handleLoadMore}
              className="px-8 py-3 rounded-xl bg-primary/10 text-primary hover:bg-primary/20 transition-colors font-medium"
            >
              {t('dashboard.showMore')} ({filteredAssets.length - visibleCount} {t('dashboard.remaining')})
            </button>
          </div>
        )}

        {filteredAssets.length === 0 && !pricesLoading && (
          <div className="text-center py-12 text-muted-foreground">
            {t('dashboard.noResults')} "{searchQuery}"
          </div>
        )}
      </main>

      <PurchaseModal
        crypto={selectedCrypto}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handlePurchaseSuccess}
      />
    </div>
  );
};

export default Dashboard;
